import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LayoutGrid, MessagesSquare, BookOpen, Building2, UserCheck, CalendarDays,
  Workflow, BarChart3, GraduationCap, Settings2, Sparkles, Send, Upload,
  Trash2, Plus, Search, Bot, Loader2, RefreshCw, Zap,
  CheckCircle2, XCircle, Clock, BellRing, Play, Plug, Save, CalendarClock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Cell,
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/metis`;

const SUBNAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "playground", label: "Playground", icon: Sparkles },
  { id: "conversations", label: "Conversations", icon: MessagesSquare },
  { id: "knowledge", label: "Knowledge Base", icon: BookOpen },
  { id: "profile", label: "Business Profile", icon: Building2 },
  { id: "leads", label: "Lead Qualification", icon: UserCheck },
  { id: "appointments", label: "Appointments", icon: CalendarDays },
  { id: "automations", label: "Automations", icon: Workflow },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "training", label: "Training", icon: GraduationCap },
  { id: "settings", label: "Settings", icon: Settings2 },
];

const card = "rounded-2xl glass p-5";
const inputCls = "brand-input";

export default function MetisConsole() {
  const [sub, setSub] = useState("overview");
  const Active = VIEWS[sub];
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(160,32,240,0.14)] text-neon-p">
          <Bot size={22} />
        </span>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Métis</h1>
          <p className="text-sm text-muted-brand">Your AI receptionist &amp; business assistant.</p>
        </div>
      </div>

      {/* Sub navigation */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        {SUBNAV.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            data-testid={`metis-tab-${s.id}`}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
              sub === s.id
                ? "glass text-white border border-[color:var(--stroke-g)] shadow-[var(--glow-g)]"
                : "glass text-muted-brand hover:text-white"
            }`}
          >
            <s.icon size={15} /> {s.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <Active />
      </div>
    </div>
  );
}

/* ============================= OVERVIEW ============================= */
function Overview() {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/overview`)).data); } catch { toast.error("Failed to load overview"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;

  const metrics = [
    ["Today's Conversations", d.todays_conversations], ["Active Visitors", d.active_visitors],
    ["Leads Captured", d.leads_captured], ["Appointments Booked", d.appointments_booked],
    ["Customer Satisfaction", `${d.customer_satisfaction}%`], ["Avg Response Time", d.avg_response_time],
    ["Escalations", d.escalations], ["AI Confidence", `${d.ai_confidence}%`],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {metrics.map(([label, val], i) => (
          <motion.div key={label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="neon-frame rounded-2xl glass p-5">
            <div className="font-display text-2xl font-semibold text-white">{val}</div>
            <div className="mt-1 text-xs text-muted-brand">{label}</div>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <RecentList title="Recent Conversations" items={d.recent_conversations} render={(c) => (<><div className="text-sm text-white">{c.visitor_name}</div><div className="truncate text-xs text-muted-brand">{c.summary || "—"}</div></>)} />
        <RecentList title="Recent Leads" items={d.recent_leads} render={(l) => (<><div className="text-sm text-white">{l.name || "Unknown"}</div><div className="text-xs text-muted-brand">{l.service_needed || l.email || "—"}</div></>)} />
        <RecentList title="Recent Appointments" items={d.recent_appointments} render={(a) => (<><div className="text-sm text-white">{a.name || "—"}</div><div className="text-xs text-muted-brand">{a.service} · {a.datetime}</div></>)} />
      </div>
    </div>
  );
}
const RecentList = ({ title, items, render }) => (
  <div className={card}>
    <h3 className="mb-3 font-display font-semibold text-white">{title}</h3>
    {(!items || items.length === 0) ? <p className="py-6 text-center text-sm text-muted-brand">No data yet.</p> :
      <ul className="space-y-2">{items.map((it, i) => <li key={i} className="rounded-xl px-3 py-2 hover:bg-[rgba(255,255,255,0.03)]">{render(it)}</li>)}</ul>}
  </div>
);

/* ============================= PLAYGROUND ============================= */
function Playground() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: text, conversation_id: convId });
      setConvId(res.data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply, confidence: res.data.confidence }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Métis is temporarily unavailable. Please try again.", error: true }]);
    } finally { setBusy(false); }
  };

  const qualify = async () => {
    if (!convId) return;
    try { const r = await axios.post(`${API}/leads/from-conversation/${convId}`); toast.success(`Lead captured: ${r.data.name || r.data.email || "record created"}`); }
    catch { toast.error("Could not qualify lead"); }
  };

  return (
    <div className={`${card} flex h-[560px] flex-col`} data-testid="metis-playground">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-brand"><span className="h-2 w-2 rounded-full bg-[color:var(--neon-g)] shadow-[0_0_8px_var(--neon-g)]" /> Métis is online</div>
        <button onClick={qualify} disabled={!convId} className="inline-flex items-center gap-1.5 rounded-lg glass px-3 py-1.5 text-xs text-white disabled:opacity-40" data-testid="metis-qualify-button"><Zap size={13} className="text-neon-g" /> Qualify lead</button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto pr-1">
        {messages.length === 0 && <p className="mt-10 text-center text-sm text-muted-brand">Say hello to Métis — ask about services, pricing, or hours.</p>}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[color:var(--neon-g)] text-black" : m.error ? "glass text-[#ff9b9b]" : "glass text-white"}`}>
              {m.text}
              {m.confidence != null && <div className="mt-1 text-[10px] text-muted-brand">confidence {m.confidence}%</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="glass rounded-2xl px-4 py-2.5 text-sm text-muted-brand"><Loader2 size={15} className="animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className={inputCls} data-testid="metis-chat-input" />
        <button onClick={send} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--neon-g)] px-4 text-sm font-semibold text-black disabled:opacity-50" data-testid="metis-send-button"><Send size={15} /></button>
      </div>
    </div>
  );
}

/* ============================= CONVERSATIONS ============================= */
function Conversations() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(async () => { try { setItems((await axios.get(`${API}/conversations`)).data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = items.filter((c) => !q || (c.visitor_name || "").toLowerCase().includes(q.toLowerCase()) || (c.summary || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <div className="relative mb-3"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-brand" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…" className={`${inputCls} pl-9`} /></div>
        {filtered.length === 0 ? <p className="py-8 text-center text-sm text-muted-brand">No conversations yet.</p> :
          <ul className="space-y-2">{filtered.map((c) => (
            <li key={c.id}><button onClick={() => setSel(c)} className={`w-full rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[rgba(255,255,255,0.03)] ${sel?.id === c.id ? "glass" : ""}`}>
              <div className="flex items-center justify-between"><span className="text-sm text-white">{c.visitor_name}</span><span className="text-[11px] text-muted-brand">{(c.messages || []).length} msgs</span></div>
              <div className="truncate text-xs text-muted-brand">{c.summary || "—"}</div>
            </button></li>))}</ul>}
      </div>
      <div className={card}>
        {!sel ? <p className="py-10 text-center text-sm text-muted-brand">Select a conversation to view the transcript.</p> :
          <div>
            <div className="mb-3 flex items-center justify-between"><h3 className="font-display font-semibold text-white">{sel.visitor_name}</h3><span className="rounded-full glass px-2.5 py-1 text-[11px] text-neon-g">conf {sel.confidence || 0}%</span></div>
            <div className="max-h-[420px] space-y-2 overflow-y-auto">{(sel.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-[rgba(57,255,20,0.12)] text-white" : "glass text-white"}`}>{m.text}</div></div>))}</div>
          </div>}
      </div>
    </div>
  );
}

/* ============================= KNOWLEDGE BASE ============================= */
function Knowledge() {
  const [data, setData] = useState({ sources: [], total_chunks: 0 });
  const [text, setText] = useState(""); const [src, setSrc] = useState("");
  const [q, setQ] = useState(""); const [results, setResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(async () => { try { setData((await axios.get(`${API}/knowledge`)).data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await axios.post(`${API}/knowledge/upload`, fd); toast.success(`Added ${r.data.chunks_added} chunks from ${r.data.source}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addText = async () => { if (!text.trim()) return; try { const r = await axios.post(`${API}/knowledge/text`, { source: src || "manual note", text }); toast.success(`Added ${r.data.chunks_added} chunks`); setText(""); setSrc(""); load(); } catch { toast.error("Failed"); } };
  const del = async (s) => { try { await axios.delete(`${API}/knowledge/${encodeURIComponent(s)}`); load(); } catch {} };
  const search = async () => { if (!q.trim()) return; try { setResults((await axios.get(`${API}/knowledge/search`, { params: { q } })).data); } catch {} };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Upload knowledge</h3>
        <p className="mb-3 text-xs text-muted-brand">PDF, DOCX, TXT, CSV — price lists, menus, policies, guides, FAQs.</p>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.csv,.md" onChange={upload} className="hidden" data-testid="metis-kb-file" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--neon-g)] px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-50" data-testid="metis-kb-upload">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload file
        </button>
        <div className="mt-5">
          <Label className="text-xs uppercase tracking-wide text-muted-brand">Or paste text</Label>
          <Input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="Source label (e.g. Pricing 2026)" className={`${inputCls} mt-2`} />
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste policies, service details, FAQs…" className={`${inputCls} mt-2 resize-none`} data-testid="metis-kb-text" />
          <button onClick={addText} className="mt-2 inline-flex items-center gap-2 rounded-lg glass px-4 py-2 text-sm text-white" data-testid="metis-kb-add-text"><Plus size={15} /> Add to knowledge</button>
        </div>
      </div>
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Knowledge sources ({data.total_chunks} chunks)</h3>
        {(!data.sources || data.sources.length === 0) ? <p className="py-6 text-center text-sm text-muted-brand">No knowledge uploaded yet.</p> :
          <ul className="space-y-2">{data.sources.map((s) => (
            <li key={s.source} className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-[rgba(255,255,255,0.03)]">
              <span className="truncate text-sm text-white">{s.source}<span className="ml-2 text-xs text-muted-brand">{s.chunks} chunks</span></span>
              <button onClick={() => del(s.source)} className="text-muted-brand hover:text-[#ff6b6b]"><Trash2 size={15} /></button>
            </li>))}</ul>}
        <div className="mt-5 border-t border-[rgba(255,255,255,0.08)] pt-4">
          <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-brand" /><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search knowledge…" className={`${inputCls} pl-9`} data-testid="metis-kb-search" /></div>
          {results.map((r, i) => <div key={i} className="mt-2 rounded-xl glass p-3 text-xs text-muted-brand"><span className="text-neon-g">{(r.score * 100).toFixed(0)}%</span> · {r.source}<p className="mt-1 text-white">{r.text.slice(0, 160)}…</p></div>)}
        </div>
      </div>
    </div>
  );
}

/* ============================= BUSINESS PROFILE ============================= */
const TONES = ["Professional", "Luxury", "Friendly", "Corporate", "Medical", "Legal", "Automotive", "Restaurant", "Church", "Educational", "Creative", "Custom"];
const SLIDERS = [["friendliness", "Friendliness"], ["professionalism", "Professionalism"], ["humor", "Humor"], ["sales_focus", "Sales Focus"], ["empathy", "Empathy"], ["conversation_length", "Conversation Length"]];

function Profile() {
  const [b, setB] = useState(null);
  useEffect(() => { (async () => { try { setB((await axios.get(`${API}/business`)).data); } catch {} })(); }, []);
  if (!b) return <Loading />;
  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));
  const setSlider = (k, v) => setB((p) => ({ ...p, personality: { ...p.personality, sliders: { ...(p.personality?.sliders || {}), [k]: v } } }));
  const save = async () => {
    const payload = { ...b };
    if (typeof payload.services === "string") payload.services = payload.services.split("\n").filter(Boolean).map((l) => { const [name, price] = l.split("|"); return { name: (name || "").trim(), price: (price || "").trim() }; });
    if (typeof payload.faqs === "string") payload.faqs = payload.faqs.split("\n").filter(Boolean).map((l) => { const [q, a] = l.split("::"); return { q: (q || "").trim(), a: (a || "").trim() }; });
    if (typeof payload.languages === "string") payload.languages = payload.languages.split(",").map((s) => s.trim()).filter(Boolean);
    try { await axios.put(`${API}/business`, payload); toast.success("Business profile saved"); } catch { toast.error("Save failed"); }
  };
  const servicesText = Array.isArray(b.services) ? b.services.map((s) => `${s.name || ""}${s.price ? " | " + s.price : ""}`).join("\n") : b.services;
  const faqsText = Array.isArray(b.faqs) ? b.faqs.map((f) => `${f.q || ""} :: ${f.a || ""}`).join("\n") : b.faqs;
  const langs = Array.isArray(b.languages) ? b.languages.join(", ") : b.languages;

  const F = ({ k, label, ...p }) => (<div><Label className="text-xs uppercase tracking-wide text-muted-brand">{label}</Label><Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${inputCls} mt-1.5`} {...p} /></div>);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-4 font-display font-semibold text-white">Business details</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <F k="name" label="Business Name" /><F k="industry" label="Industry" />
          <F k="phone" label="Phone" /><F k="email" label="Email" />
          <F k="website" label="Website" /><F k="timezone" label="Time Zone" />
          <F k="business_hours" label="Business Hours" /><F k="holiday_hours" label="Holiday Hours" />
          <F k="emergency_contact" label="Emergency Contact" />
          <div><Label className="text-xs uppercase tracking-wide text-muted-brand">Languages (comma sep)</Label><Input defaultValue={langs} onChange={(e) => set("languages", e.target.value)} className={`${inputCls} mt-1.5`} /></div>
        </div>
        <div className="mt-4"><Label className="text-xs uppercase tracking-wide text-muted-brand">Address</Label><Input value={b.address || ""} onChange={(e) => set("address", e.target.value)} className={`${inputCls} mt-1.5`} /></div>
        <div className="mt-4"><Label className="text-xs uppercase tracking-wide text-muted-brand">Services (one per line: Name | Price)</Label><Textarea defaultValue={servicesText} onChange={(e) => set("services", e.target.value)} rows={3} className={`${inputCls} mt-1.5 resize-none`} /></div>
        <div className="mt-4"><Label className="text-xs uppercase tracking-wide text-muted-brand">FAQs (one per line: Question :: Answer)</Label><Textarea defaultValue={faqsText} onChange={(e) => set("faqs", e.target.value)} rows={3} className={`${inputCls} mt-1.5 resize-none`} /></div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><Label className="text-xs uppercase tracking-wide text-muted-brand">Mission</Label><Textarea value={b.mission || ""} onChange={(e) => set("mission", e.target.value)} rows={2} className={`${inputCls} mt-1.5 resize-none`} /></div>
          <div><Label className="text-xs uppercase tracking-wide text-muted-brand">Values</Label><Textarea value={b.values || ""} onChange={(e) => set("values", e.target.value)} rows={2} className={`${inputCls} mt-1.5 resize-none`} /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="mb-4 font-display font-semibold text-white">AI Personality</h3>
        <Label className="text-xs uppercase tracking-wide text-muted-brand">Preset tone</Label>
        <Select value={b.personality?.tone || "Professional"} onValueChange={(v) => set("personality", { ...b.personality, tone: v })}>
          <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="metis-tone-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <div className="mt-5 space-y-5">
          {SLIDERS.map(([k, label]) => {
            const val = b.personality?.sliders?.[k] ?? 50;
            return (<div key={k}><div className="mb-2 flex justify-between text-xs"><span className="text-muted-brand">{label}</span><span className="text-neon-g">{val}</span></div>
              <Slider value={[val]} min={0} max={100} step={5} onValueChange={(v) => setSlider(k, v[0])} data-testid={`metis-slider-${k}`} /></div>);
          })}
        </div>
        <button onClick={save} className="mt-6 w-full rounded-lg bg-[color:var(--neon-g)] px-4 py-2.5 text-sm font-semibold text-black" data-testid="metis-save-profile">Save profile &amp; personality</button>
      </div>
    </div>
  );
}

/* ============================= LEADS ============================= */
function Leads() {
  const [qs, setQs] = useState([]); const [leads, setLeads] = useState([]);
  const load = useCallback(async () => {
    try { setQs((await axios.get(`${API}/qualification`)).data.questions || []); } catch {}
    try { setLeads((await axios.get(`${API}/leads`)).data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (i) => { const next = qs.map((q, idx) => idx === i ? { ...q, enabled: !q.enabled } : q); setQs(next); try { await axios.put(`${API}/qualification`, { questions: next }); } catch {} };
  const setStatus = async (id, status) => { try { await axios.patch(`${API}/leads/${id}`, { status }); setLeads((p) => p.map((l) => l.id === id ? { ...l, status } : l)); } catch {} };
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Qualification questions</h3>
        <ul className="space-y-2">{qs.map((q, i) => (
          <li key={q.field} className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-[rgba(255,255,255,0.03)]">
            <span className="text-sm text-white">{q.field}{q.required && <span className="ml-2 text-[10px] text-neon-g">required</span>}</span>
            <Switch checked={q.enabled} onCheckedChange={() => toggle(i)} data-testid={`metis-qual-${q.field}`} />
          </li>))}</ul>
      </div>
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Captured leads ({leads.length})</h3>
        {leads.length === 0 ? <p className="py-6 text-center text-sm text-muted-brand">No leads captured yet.</p> :
          <ul className="space-y-2">{leads.map((l) => (
            <li key={l.id} className="rounded-xl px-3 py-2.5 hover:bg-[rgba(255,255,255,0.03)]">
              <div className="flex items-center justify-between"><span className="text-sm text-white">{l.name || "Unknown"}</span>
                <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}><SelectTrigger className={`${inputCls} h-8 w-32`}><SelectValue /></SelectTrigger><SelectContent>{["new", "contacted", "qualified", "won", "lost"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="text-xs text-muted-brand">{[l.email, l.phone, l.service_needed].filter(Boolean).join(" · ") || "—"}</div>
            </li>))}</ul>}
      </div>
    </div>
  );
}

/* ============================= APPOINTMENTS ============================= */
const WEEK = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];
const APPT_STATUS = ["scheduled", "confirmed", "completed", "cancelled", "missed"];
const statusTone = {
  sent: "text-neon-g", confirmed: "text-neon-g", scheduled: "text-neon-p",
  pending: "text-muted-brand", completed: "text-neon-g", cancelled: "text-[#ff9b9b]",
  missed: "text-[#ff9b9b]", simulated: "text-[#F5C542]", queued: "text-[#F5C542]",
  not_connected: "text-muted-brand", failed: "text-[#ff6b6b]",
};

function Appointments() {
  const [items, setItems] = useState([]);
  const [av, setAv] = useState(null);
  const [providers, setProviders] = useState({ calendars: [] });
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", service: "", start_at: "" });

  const loadItems = useCallback(async () => { try { setItems((await axios.get(`${API}/appointments`)).data); } catch {} }, []);
  const loadAv = useCallback(async () => { try { setAv((await axios.get(`${API}/scheduling/availability`)).data); } catch {} }, []);
  const loadProviders = useCallback(async () => { try { setProviders((await axios.get(`${API}/providers`)).data); } catch {} }, []);
  useEffect(() => { loadItems(); loadAv(); loadProviders(); }, [loadItems, loadAv, loadProviders]);

  const fetchSlots = useCallback(async (d) => {
    if (!d) return;
    setSlotBusy(true);
    try { setSlots((await axios.get(`${API}/scheduling/slots`, { params: { date: d } })).data.slots || []); }
    catch { setSlots([]); }
    finally { setSlotBusy(false); }
  }, []);

  const onDate = (d) => { setDate(d); setF((p) => ({ ...p, start_at: "" })); fetchSlots(d); };

  const saveAv = async () => {
    try { await axios.put(`${API}/scheduling/availability`, av); toast.success("Availability saved"); if (date) fetchSlots(date); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleDay = (day) => setAv((p) => {
    const weekly = { ...(p.weekly || {}) };
    weekly[day] = (weekly[day] && weekly[day].length) ? [] : [{ start: "09:00", end: "17:00" }];
    return { ...p, weekly };
  });
  const setWindow = (day, key, val) => setAv((p) => {
    const weekly = { ...(p.weekly || {}) };
    const w = weekly[day]?.[0] || { start: "09:00", end: "17:00" };
    weekly[day] = [{ ...w, [key]: val }];
    return { ...p, weekly };
  });

  const book = async () => {
    if (!f.name || !f.start_at) { toast.error("Name and a time slot are required"); return; }
    try {
      await axios.post(`${API}/appointments`, { ...f, duration_minutes: av?.slot_minutes || 30 });
      toast.success("Appointment booked & confirmation queued");
      setF({ name: "", email: "", service: "", start_at: "" });
      loadItems(); fetchSlots(date);
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
  };

  const setStatus = async (id, status) => { try { await axios.patch(`${API}/appointments/${id}`, { status }); loadItems(); } catch {} };
  const del = async (id) => { try { await axios.delete(`${API}/appointments/${id}`); loadItems(); } catch {} };
  const sendConf = async (id) => { try { const r = await axios.post(`${API}/appointments/${id}/send-confirmation`); toast.success(`Confirmation: ${r.data.status}`); loadItems(); } catch { toast.error("Failed"); } };
  const sendRem = async (id) => { try { const r = await axios.post(`${API}/appointments/${id}/send-reminder`); toast.success(`Reminder: ${r.data.status}`); loadItems(); } catch { toast.error("Failed"); } };
  const processReminders = async () => { try { const r = await axios.post(`${API}/scheduling/process-reminders`); toast.success(`Reminders — due ${r.data.due}, sent ${r.data.sent}`); loadItems(); } catch { toast.error("Failed"); } };

  if (!av) return <Loading />;

  return (
    <div className="space-y-4">
      {/* Provider badges */}
      <div className={card} data-testid="metis-appt-providers">
        <div className="mb-3 flex items-center gap-2"><Plug size={15} className="text-neon-p" /><h3 className="font-display font-semibold text-white">Calendar connections</h3></div>
        <div className="flex flex-wrap gap-2">
          {(providers.calendars || []).map((c) => (
            <span key={c.key} className="flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs">
              <span className={`h-2 w-2 rounded-full ${c.connected ? "bg-[color:var(--neon-g)] shadow-[0_0_8px_var(--neon-g)]" : "bg-[rgba(255,255,255,0.2)]"}`} />
              <span className="text-white">{c.label}</span>
              <span className={c.connected ? "text-neon-g" : "text-muted-brand"}>{c.connected ? (c.native ? "Native" : "Connected") : "Connect later"}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Availability editor */}
        <div className={card} data-testid="metis-availability">
          <div className="mb-3 flex items-center gap-2"><CalendarClock size={16} className="text-neon-g" /><h3 className="font-display font-semibold text-white">Availability</h3></div>
          <div className="space-y-2">
            {WEEK.map(([key, label]) => {
              const open = !!(av.weekly?.[key] && av.weekly[key].length);
              const w = av.weekly?.[key]?.[0] || { start: "09:00", end: "17:00" };
              return (
                <div key={key} className="flex items-center gap-3 rounded-xl px-2 py-1.5">
                  <div className="flex w-24 items-center gap-2">
                    <Switch checked={open} onCheckedChange={() => toggleDay(key)} data-testid={`metis-avail-${key}`} />
                    <span className="text-sm text-white">{label}</span>
                  </div>
                  {open ? (
                    <div className="flex items-center gap-2">
                      <Input type="time" value={w.start} onChange={(e) => setWindow(key, "start", e.target.value)} className={`${inputCls} h-8 w-28`} />
                      <span className="text-muted-brand">–</span>
                      <Input type="time" value={w.end} onChange={(e) => setWindow(key, "end", e.target.value)} className={`${inputCls} h-8 w-28`} />
                    </div>
                  ) : <span className="text-xs text-muted-brand">Closed</span>}
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-brand">Slot length (min)</Label>
              <Input type="number" min={5} step={5} value={av.slot_minutes} onChange={(e) => setAv({ ...av, slot_minutes: parseInt(e.target.value || "30", 10) })} className={`${inputCls} mt-1.5`} data-testid="metis-slot-minutes" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-brand">Buffer (min)</Label>
              <Input type="number" min={0} step={5} value={av.buffer_minutes} onChange={(e) => setAv({ ...av, buffer_minutes: parseInt(e.target.value || "0", 10) })} className={`${inputCls} mt-1.5`} />
            </div>
          </div>
          <button onClick={saveAv} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[color:var(--neon-g)] px-4 py-2.5 text-sm font-semibold text-black" data-testid="metis-save-availability"><Save size={15} /> Save availability</button>
        </div>

        {/* Booking */}
        <div className={card} data-testid="metis-booking">
          <div className="mb-3 flex items-center gap-2"><CalendarDays size={16} className="text-neon-p" /><h3 className="font-display font-semibold text-white">Book appointment</h3></div>
          <Label className="text-xs uppercase tracking-wide text-muted-brand">Date</Label>
          <Input type="date" value={date} onChange={(e) => onDate(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="metis-book-date" />
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-wide text-muted-brand">Available slots</Label>
            {slotBusy ? <div className="py-4"><Loader2 size={16} className="animate-spin text-muted-brand" /></div> :
              !date ? <p className="mt-2 text-xs text-muted-brand">Pick a date to see open times.</p> :
                slots.length === 0 ? <p className="mt-2 text-xs text-muted-brand">No open slots for this day.</p> :
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="metis-slots">
                    {slots.map((s) => (
                      <button key={s.start} onClick={() => setF({ ...f, start_at: s.start })}
                        className={`rounded-lg px-3 py-1.5 text-sm transition-all ${f.start_at === s.start ? "bg-[color:var(--neon-g)] text-black" : "glass text-white hover:text-neon-g"}`}
                        data-testid={`metis-slot-${s.label}`}>{s.label}</button>
                    ))}
                  </div>}
          </div>
          <div className="mt-4 space-y-3">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Client name" className={inputCls} data-testid="metis-book-name" />
            <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email (for confirmation)" className={inputCls} />
            <Input value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} placeholder="Service" className={inputCls} />
            <button onClick={book} disabled={!f.start_at} className="w-full rounded-lg bg-[color:var(--neon-g)] px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-50" data-testid="metis-book-submit">
              {f.start_at ? `Book ${f.start_at.replace("T", " · ")}` : "Select a slot to book"}
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className={card}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display font-semibold text-white">Upcoming ({items.length})</h3>
          <button onClick={processReminders} className="inline-flex items-center gap-1.5 rounded-lg glass px-3 py-1.5 text-xs text-white" data-testid="metis-process-reminders"><BellRing size={13} className="text-neon-g" /> Send due reminders</button>
        </div>
        {items.length === 0 ? <p className="py-6 text-center text-sm text-muted-brand">No appointments.</p> :
          <ul className="space-y-2">{items.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl glass p-4" data-testid={`metis-appt-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white">{a.name} <span className="text-muted-brand">· {a.service || "—"}</span></div>
                <div className="text-xs text-muted-brand">{(a.start_at || a.datetime || "").replace("T", " · ")}</div>
                <div className="mt-1 flex gap-3 text-[11px]">
                  <span className={statusTone[a.confirmation_status] || "text-muted-brand"}>conf: {a.confirmation_status || "pending"}</span>
                  <span className={statusTone[a.reminder_status] || "text-muted-brand"}>remind: {a.reminder_status || "pending"}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => sendConf(a.id)} title="Send confirmation" className="rounded-lg glass p-2 text-neon-g hover:shadow-[var(--glow-g)]" data-testid={`metis-appt-confirm-${a.id}`}><CheckCircle2 size={15} /></button>
                <button onClick={() => sendRem(a.id)} title="Send reminder" className="rounded-lg glass p-2 text-neon-p" data-testid={`metis-appt-remind-${a.id}`}><BellRing size={15} /></button>
                <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}><SelectTrigger className={`${inputCls} h-8 w-32`}><SelectValue /></SelectTrigger><SelectContent>{APPT_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
                <button onClick={() => del(a.id)} className="text-muted-brand hover:text-[#ff6b6b]"><Trash2 size={15} /></button>
              </div>
            </li>))}</ul>}
      </div>
    </div>
  );
}

/* ============================= AUTOMATIONS ============================= */
const CHANNELS = ["email", "sms", "whatsapp", "slack", "teams", "discord", "webhook"];

function Automations() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [providers, setProviders] = useState({ channels: [] });
  const [testing, setTesting] = useState(null);

  const loadItems = useCallback(async () => { try { setItems((await axios.get(`${API}/automations`)).data); } catch {} }, []);
  const loadRuns = useCallback(async () => { try { setRuns((await axios.get(`${API}/automations/runs`, { params: { limit: 30 } })).data); } catch {} }, []);
  const loadProviders = useCallback(async () => { try { setProviders((await axios.get(`${API}/providers`)).data); } catch {} }, []);
  useEffect(() => { loadItems(); loadRuns(); loadProviders(); }, [loadItems, loadRuns, loadProviders]);

  const toggle = async (a) => { try { await axios.patch(`${API}/automations/${a.id}`, { enabled: !a.enabled }); setItems((p) => p.map((x) => x.id === a.id ? { ...x, enabled: !x.enabled } : x)); } catch {} };
  const setChannel = async (a, channel) => { try { await axios.patch(`${API}/automations/${a.id}`, { channel }); setItems((p) => p.map((x) => x.id === a.id ? { ...x, channel } : x)); } catch {} };
  const test = async (a) => {
    setTesting(a.id);
    try { const r = await axios.post(`${API}/automations/test`, { trigger: a.trigger, automation_id: a.id }); toast.success(`Ran ${r.data.runs_executed} run(s) — ${r.data.runs?.[0]?.status || "done"}`); loadRuns(); }
    catch { toast.error("Test failed"); }
    finally { setTesting(null); }
  };

  const channelStatus = (key) => (providers.channels || []).find((c) => c.key === key);

  return (
    <div className="space-y-4">
      {/* Channel connections */}
      <div className={card} data-testid="metis-automation-providers">
        <div className="mb-3 flex items-center gap-2"><Plug size={15} className="text-neon-p" /><h3 className="font-display font-semibold text-white">Channel connections</h3></div>
        <div className="flex flex-wrap gap-2">
          {(providers.channels || []).map((c) => (
            <span key={c.key} className="flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs">
              <span className={`h-2 w-2 rounded-full ${c.connected ? "bg-[color:var(--neon-g)] shadow-[0_0_8px_var(--neon-g)]" : "bg-[rgba(255,255,255,0.2)]"}`} />
              <span className="text-white capitalize">{c.label}</span>
              <span className={c.connected ? "text-neon-g" : "text-muted-brand"}>{c.connected ? (c.native ? "Native" : "Connected") : "Connect later"}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Automations config */}
      <div className={card}>
        <h3 className="mb-1 font-display font-semibold text-white">Workflow automations</h3>
        <p className="mb-4 text-xs text-muted-brand">The native engine runs enabled automations on each event and logs every run below. Email is fully functional; other channels are pluggable adapters.</p>
        <ul className="space-y-2">{items.map((a) => {
          const cs = channelStatus(a.channel);
          return (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl glass p-4" data-testid={`metis-automation-${a.trigger}`}>
              <div className="min-w-0">
                <div className="text-sm text-white">{a.name}</div>
                <div className="text-xs text-muted-brand">trigger: {a.trigger}{cs && !cs.connected && <span className="ml-2 text-[#F5C542]">· {cs.label} not connected</span>}</div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => test(a)} disabled={testing === a.id} className="inline-flex items-center gap-1.5 rounded-lg glass px-3 py-1.5 text-xs text-white disabled:opacity-50" data-testid={`metis-automation-test-${a.trigger}`}>
                  {testing === a.id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} className="text-neon-g" />} Test
                </button>
                <Select value={a.channel} onValueChange={(v) => setChannel(a, v)}><SelectTrigger className={`${inputCls} h-8 w-32`}><SelectValue /></SelectTrigger><SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select>
                <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`metis-automation-toggle-${a.trigger}`} />
              </div>
            </li>
          );
        })}</ul>
      </div>

      {/* Runs log */}
      <div className={card} data-testid="metis-automation-runs">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display font-semibold text-white">Run history ({runs.length})</h3>
          <button onClick={loadRuns} className="rounded-lg glass p-2 text-muted-brand hover:text-white"><RefreshCw size={14} /></button>
        </div>
        {runs.length === 0 ? <p className="py-6 text-center text-sm text-muted-brand">No automation runs yet. Enable an automation or use “Test”.</p> :
          <ul className="space-y-2">{runs.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-[rgba(255,255,255,0.03)]">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm text-white">
                  {r.status === "sent" ? <CheckCircle2 size={14} className="text-neon-g" /> : r.status === "failed" ? <XCircle size={14} className="text-[#ff6b6b]" /> : <Clock size={14} className="text-[#F5C542]" />}
                  {r.automation_name} <span className="text-xs text-muted-brand">· {r.channel}</span>
                </div>
                <div className="truncate text-xs text-muted-brand">{r.detail || r.payload_summary}</div>
              </div>
              <span className={`shrink-0 text-xs ${statusTone[r.status] || "text-muted-brand"}`}>{r.status}</span>
            </li>))}</ul>}
      </div>
    </div>
  );
}

/* ============================= ANALYTICS ============================= */
function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { setD((await axios.get(`${API}/analytics`)).data); } catch {} })(); }, []);
  if (!d) return <Loading />;
  const barColors = ["#39FF14", "#A020F0"];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-4 font-display font-semibold text-white">Conversations (7 days)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.conversations_by_day}><XAxis dataKey="date" stroke="#B8B8B8" fontSize={11} /><YAxis stroke="#B8B8B8" fontSize={11} allowDecimals={false} /><RTooltip contentStyle={{ background: "#121214", border: "1px solid rgba(57,255,20,0.2)", borderRadius: 12, color: "#fff" }} /><Bar dataKey="count" radius={[6, 6, 0, 0]}>{d.conversations_by_day.map((e, i) => <Cell key={i} fill="#39FF14" />)}</Bar></BarChart>
        </ResponsiveContainer>
      </div>
      <div className={card}>
        <h3 className="mb-4 font-display font-semibold text-white">Peak hours</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.peak_hours}><XAxis dataKey="hour" stroke="#B8B8B8" fontSize={10} interval={2} /><YAxis stroke="#B8B8B8" fontSize={11} allowDecimals={false} /><RTooltip contentStyle={{ background: "#121214", border: "1px solid rgba(160,32,240,0.2)", borderRadius: 12, color: "#fff" }} /><Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#A020F0" /></BarChart>
        </ResponsiveContainer>
      </div>
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Lead conversion</h3>
        <div className="font-display text-4xl font-semibold text-neon-g">{d.lead_conversion}%</div>
        <p className="mt-1 text-xs text-muted-brand">{d.total_leads} leads from {d.total_conversations} conversations</p>
      </div>
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Lead sources</h3>
        {(d.lead_sources || []).length === 0 ? <p className="py-6 text-center text-sm text-muted-brand">No leads yet.</p> :
          <ul className="space-y-2">{d.lead_sources.map((s) => <li key={s.name} className="flex justify-between text-sm"><span className="text-muted-brand capitalize">{s.name}</span><span className="text-white">{s.count}</span></li>)}</ul>}
      </div>
    </div>
  );
}

/* ============================= TRAINING ============================= */
function Training() {
  const [items, setItems] = useState([]); const [text, setText] = useState("");
  const load = useCallback(async () => { try { setItems((await axios.get(`${API}/training`)).data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!text.trim()) return; try { await axios.post(`${API}/training`, { instruction: text }); setText(""); load(); toast.success("Métis learned something new"); } catch {} };
  const del = async (id) => { try { await axios.delete(`${API}/training/${id}`); load(); } catch {} };
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-1 font-display font-semibold text-white">Teach Métis</h3>
        <p className="mb-3 text-xs text-muted-brand">Type instructions in plain language. No prompt engineering required.</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={'e.g. "When someone asks about ceramic coating, explain the 3-year and 5-year packages."'} className={`${inputCls} resize-none`} data-testid="metis-training-input" />
        <button onClick={add} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[color:var(--neon-g)] px-4 py-2.5 text-sm font-semibold text-black" data-testid="metis-training-add"><Plus size={15} /> Teach Métis</button>
      </div>
      <div className={card}>
        <h3 className="mb-3 font-display font-semibold text-white">Learned instructions ({items.length})</h3>
        {items.length === 0 ? <p className="py-6 text-center text-sm text-muted-brand">Nothing taught yet.</p> :
          <ul className="space-y-2">{items.map((t) => (
            <li key={t.id} className="flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-[rgba(255,255,255,0.03)]">
              <span className="text-sm text-white">{t.instruction}</span>
              <button onClick={() => del(t.id)} className="mt-0.5 shrink-0 text-muted-brand hover:text-[#ff6b6b]"><Trash2 size={14} /></button>
            </li>))}</ul>}
      </div>
    </div>
  );
}

/* ============================= SETTINGS ============================= */
function SettingsView() {
  const [s, setS] = useState(null);
  useEffect(() => { (async () => { try { setS((await axios.get(`${API}/settings`)).data); } catch {} })(); }, []);
  if (!s) return <Loading />;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await axios.put(`${API}/settings`, s); toast.success("Settings saved"); } catch { toast.error("Save failed"); } };
  const T = ({ k, label, rows = 2, ...p }) => (<div><Label className="text-xs uppercase tracking-wide text-muted-brand">{label}</Label><Textarea value={s[k] || ""} onChange={(e) => set(k, e.target.value)} rows={rows} className={`${inputCls} mt-1.5 resize-none`} {...p} /></div>);
  return (
    <div className={card}>
      <h3 className="mb-4 font-display font-semibold text-white">Métis settings</h3>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <T k="greeting" label="Greeting Message" />
        <T k="fallback" label="Fallback Response" />
        <T k="escalation_rules" label="Escalation Rules" />
        <T k="business_hours" label="Business Hours" />
        <T k="lead_routing" label="Lead Routing" />
        <div><Label className="text-xs uppercase tracking-wide text-muted-brand">CRM Integration</Label>
          <Select value={s.crm_integration || ""} onValueChange={(v) => set("crm_integration", v)}><SelectTrigger className={`${inputCls} mt-1.5`}><SelectValue placeholder="Select CRM (connect later)" /></SelectTrigger><SelectContent>{["HubSpot", "Salesforce", "Pipedrive", "Zoho", "None"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <button onClick={save} className="mt-6 rounded-lg bg-[color:var(--neon-g)] px-5 py-2.5 text-sm font-semibold text-black" data-testid="metis-save-settings">Save settings</button>
    </div>
  );
}

const Loading = () => <div className="flex items-center justify-center py-16 text-muted-brand"><Loader2 className="animate-spin" /></div>;

const VIEWS = {
  overview: Overview, playground: Playground, conversations: Conversations, knowledge: Knowledge,
  profile: Profile, leads: Leads, appointments: Appointments, automations: Automations,
  analytics: Analytics, training: Training, settings: SettingsView,
};
